/**
 * AuthorityModal Component
 * 권한 생성/수정 모달
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { AuthorityModalProps, AuthorityFormData, AuthorityFormMode } from '../types/authority.types';
import { BASE_USER_TYPE_OPTIONS, getGroupedPageOptions } from '../utils/authorityPageUtils';
import '../styles/AuthorityModal.css';

const MODAL_TITLES: Record<AuthorityFormMode, string> = {
    create: '권한 등록',
    edit: '권한 수정',
};

const MODAL_SUBTITLES: Record<AuthorityFormMode, string> = {
    create: '새 권한을 시스템에 등록합니다',
    edit: '권한 정보를 수정합니다',
};

const USER_TYPE_ICONS: Record<string, string> = {
    C: 'fa-building',
    S: 'fa-handshake',
    R: 'fa-motorcycle',
};

const INITIAL_FORM_DATA: AuthorityFormData = {
    prmCode: '',
    prmName: '',
    prmDesc: '',
    baseUserType: '',
    accessPages: [],
};

const AuthorityModal: React.FC<AuthorityModalProps> = ({
                                                           isOpen,
                                                           mode,
                                                           authority,
                                                           onClose,
                                                           onSave,
                                                       }) => {
    const { showToast } = useToast();
    const [formData, setFormData] = useState<AuthorityFormData>(INITIAL_FORM_DATA);
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof AuthorityFormData, string>>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (mode === 'edit' && authority) {
                setFormData({
                    prmCode: authority.prmCode,
                    prmName: authority.prmName,
                    prmDesc: authority.prmDesc || '',
                    baseUserType: authority.baseUserType || '',
                    accessPages: authority.accessPages || [],
                });
            } else {
                setFormData(INITIAL_FORM_DATA);
            }
            setFieldErrors({});
        }
    }, [isOpen, mode, authority]);

    const groupedPageOptions = useMemo(() => getGroupedPageOptions(), []);
    const isEditMode = mode === 'edit';

    const handleChange = useCallback((field: keyof AuthorityFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (value.trim()) {
            setFieldErrors(prev => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    }, []);

    const handleBlur = useCallback((field: keyof AuthorityFormData) => {
        setFieldErrors(prev => {
            const next = { ...prev };
            const value = formData[field];
            const strVal = Array.isArray(value) ? '' : value;
            if (!strVal.trim()) {
                const messages: Partial<Record<keyof AuthorityFormData, string>> = {
                    prmCode: '권한코드는 필수입니다.',
                    prmName: '권한명은 필수입니다.',
                    baseUserType: '기반 사용자 유형을 선택하세요.',
                };
                if (messages[field]) next[field] = messages[field]!;
            } else {
                delete next[field];
            }
            return next;
        });
    }, [formData]);

    const handlePageSelect = useCallback((pageId: string, isParent: boolean, children?: string[]) => {
        setFormData(prev => {
            const currentPages = new Set(prev.accessPages);
            if (currentPages.has(pageId)) {
                currentPages.delete(pageId);
                if (isParent && children) {
                    children.forEach(childPage => {
                        groupedPageOptions.forEach(group => {
                            group.children.forEach(child => {
                                if (child.page === childPage) currentPages.delete(child.id);
                            });
                        });
                    });
                }
            } else {
                currentPages.add(pageId);
                if (isParent && children) {
                    groupedPageOptions.forEach(group => {
                        if (group.parent.id === pageId) {
                            group.children.forEach(child => currentPages.add(child.id));
                        }
                    });
                }
            }
            const nextPages = Array.from(currentPages);
            if (nextPages.length > 0) {
                setFieldErrors(prev => {
                    const next = { ...prev };
                    delete next.accessPages;
                    return next;
                });
            }
            return { ...prev, accessPages: nextPages };
        });
    }, [groupedPageOptions]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        const errors: Partial<Record<keyof AuthorityFormData, string>> = {};
        if (!formData.prmCode.trim()) errors.prmCode = '권한코드는 필수입니다.';
        if (!formData.prmName.trim()) errors.prmName = '권한명은 필수입니다.';
        if (!formData.baseUserType.trim()) errors.baseUserType = '기반 사용자 유형을 선택하세요.';
        if (formData.accessPages.length === 0) errors.accessPages = '접근 페이지를 1개 이상 선택하세요.';
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            showToast('필수 항목을 입력해주세요.', { type: ToastType.ERROR });
            return;
        }
        setIsSubmitting(true);
        try {
            await onSave(formData);
        } catch {
            showToast(
                mode === 'create' ? '권한 등록에 실패했습니다.' : '권한 수정에 실패했습니다.',
                { type: ToastType.ERROR }
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, mode, onSave, showToast]);

    const handleBackdropClick = useCallback((e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    }, [onClose]);

    const isPageSelected = useCallback((pageId: string) =>
            formData.accessPages.includes(pageId),
        [formData.accessPages]);

    const isAllChildrenSelected = useCallback((parentId: string) => {
        const group = groupedPageOptions.find(g => g.parent.id === parentId);
        if (!group || group.children.length === 0) return false;
        return group.children.every(child => formData.accessPages.includes(child.id));
    }, [formData.accessPages, groupedPageOptions]);

    if (!isOpen) return null;

    return (
        <div className="am-overlay" onClick={handleBackdropClick}>
            <div className="am-modal">

                {/* ── 헤더 ───────────────────────────────────── */}
                <div className="am-header">
                    <div className="am-header-icon">
                        <i className={`fas ${mode === 'create' ? 'fa-shield-alt' : 'fa-pen'}`} />
                    </div>
                    <div className="am-header-text">
                        <h2 className="am-title">{MODAL_TITLES[mode]}</h2>
                        <p className="am-subtitle">{MODAL_SUBTITLES[mode]}</p>
                    </div>
                    <button className="am-close-btn" type="button" onClick={onClose}>
                        <i className="fas fa-times" />
                    </button>
                </div>

                {/* ── 바디 ───────────────────────────────────── */}
                <div className="am-body">
                    <form id="am-form" onSubmit={handleSubmit} noValidate>

                        {/* 섹션: 기본 정보 */}
                        <div className="am-section">
                            <div className="am-section-label">기본 정보</div>

                            {/* 권한코드 + 권한명 (2열) */}
                            <div className="am-row">
                                <div className="am-field">
                                    <label className="am-label">
                                        권한코드
                                        <span className="am-required">*</span>
                                    </label>
                                    <div className={`am-input-wrap${fieldErrors.prmCode ? ' is-error' : ''}${isEditMode ? ' is-disabled' : ''}`}>
                                        <i className="fas fa-key am-icon-left" />
                                        <input
                                            type="text"
                                            className="am-input"
                                            placeholder="예: CUSTOM1"
                                            value={formData.prmCode}
                                            onChange={(e) => handleChange('prmCode', e.target.value.toUpperCase())}
                                            onBlur={() => handleBlur('prmCode')}
                                            maxLength={10}
                                            disabled={isEditMode}
                                        />
                                        {isEditMode && <i className="fas fa-lock am-icon-right" />}
                                    </div>
                                    {isEditMode && (
                                        <span className="am-hint am-hint-warning">
                                            <i className="fas fa-info-circle" /> 수정 불가
                                        </span>
                                    )}
                                    {fieldErrors.prmCode && (
                                        <span className="am-error-msg">
                                            <i className="fas fa-exclamation-circle" /> {fieldErrors.prmCode}
                                        </span>
                                    )}
                                </div>

                                <div className="am-field">
                                    <label className="am-label">
                                        권한명
                                        <span className="am-required">*</span>
                                    </label>
                                    <div className={`am-input-wrap${fieldErrors.prmName ? ' is-error' : ''}`}>
                                        <i className="fas fa-tag am-icon-left" />
                                        <input
                                            type="text"
                                            className="am-input"
                                            placeholder="권한명을 입력하세요"
                                            value={formData.prmName}
                                            onChange={(e) => handleChange('prmName', e.target.value)}
                                            onBlur={() => handleBlur('prmName')}
                                            maxLength={50}
                                        />
                                    </div>
                                    {fieldErrors.prmName && (
                                        <span className="am-error-msg">
                                            <i className="fas fa-exclamation-circle" /> {fieldErrors.prmName}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* 권한설명 */}
                            <div className="am-field">
                                <label className="am-label">
                                    권한설명
                                    <span className="am-char-count">{formData.prmDesc.length}/200</span>
                                </label>
                                <textarea
                                    className="am-textarea"
                                    placeholder="권한에 대한 설명을 입력하세요 (선택)"
                                    value={formData.prmDesc}
                                    onChange={(e) => handleChange('prmDesc', e.target.value)}
                                    rows={2}
                                    maxLength={200}
                                />
                            </div>
                        </div>

                        {/* 섹션: 기반 사용자 유형 */}
                        <div className="am-section">
                            <div className="am-section-label">
                                기반 사용자 유형
                                <span className="am-required">*</span>
                            </div>
                            <div className={`am-user-cards${fieldErrors.baseUserType ? ' is-error' : ''}`}>
                                {BASE_USER_TYPE_OPTIONS.map(option => {
                                    const selected = formData.baseUserType === option.value;
                                    return (
                                        <button
                                            key={option.value}
                                            type="button"
                                            className={`am-user-card am-user-card-${option.value.toLowerCase()}${selected ? ' selected' : ''}`}
                                            onClick={() => handleChange('baseUserType', option.value)}
                                        >
                                            <div className="am-user-icon">
                                                <i className={`fas ${USER_TYPE_ICONS[option.value]}`} />
                                            </div>
                                            <span className="am-user-label">{option.label}</span>
                                            <span className={`am-user-dot${selected ? ' selected' : ''}`} />
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="am-hint">정산, 조회 등 비즈니스 로직에 영향을 줍니다.</p>
                            {fieldErrors.baseUserType && (
                                <span className="am-error-msg">
                                    <i className="fas fa-exclamation-circle" /> {fieldErrors.baseUserType}
                                </span>
                            )}
                        </div>

                        {/* 섹션: 접근 페이지 */}
                        <div className="am-section am-section-last">
                            <div className="am-section-title-row">
                                <div className="am-section-label">
                                    접근 페이지
                                    <span className="am-required">*</span>
                                </div>
                                <span className={`am-page-badge${formData.accessPages.length > 0 ? ' active' : ''}`}>
                                    {formData.accessPages.length > 0
                                        ? `${formData.accessPages.length}개 선택됨`
                                        : '미선택'}
                                </span>
                            </div>

                            <div className={`am-page-list${fieldErrors.accessPages ? ' is-error' : ''}`}>
                                {groupedPageOptions.map(({ parent, children }) => {
                                    const parentSelected = isPageSelected(parent.id) || isAllChildrenSelected(parent.id);
                                    return (
                                        <div key={parent.id} className="am-page-group">
                                            <div
                                                className={`am-page-row am-parent-row${parentSelected ? ' selected' : ''}`}
                                                onClick={() => handlePageSelect(parent.id, true, parent.children)}
                                            >
                                                <span className={`am-checkbox${parentSelected ? ' checked' : ''}`}>
                                                    {parentSelected && <i className="fas fa-check" />}
                                                </span>
                                                <span className="am-page-name">{parent.label}</span>
                                                {children.length > 0 && (
                                                    <span className="am-count-chip">{children.length}</span>
                                                )}
                                            </div>

                                            {children.length > 0 && (
                                                <div className="am-children">
                                                    {children.map(child => {
                                                        const childSelected = isPageSelected(child.id);
                                                        return (
                                                            <div
                                                                key={child.id}
                                                                className={`am-page-row am-child-row${childSelected ? ' selected' : ''}`}
                                                                onClick={() => handlePageSelect(child.id, false)}
                                                            >
                                                                <span className={`am-checkbox am-checkbox-sm${childSelected ? ' checked' : ''}`}>
                                                                    {childSelected && <i className="fas fa-check" />}
                                                                </span>
                                                                <span className="am-page-name">{child.label}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {fieldErrors.accessPages && (
                                <span className="am-error-msg">
                                    <i className="fas fa-exclamation-circle" /> {fieldErrors.accessPages}
                                </span>
                            )}
                        </div>
                    </form>
                </div>

                {/* ── 푸터 ───────────────────────────────────── */}
                <div className="am-footer">
                    <button type="button" className="am-btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button type="submit" form="am-form" className="am-btn-save" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <i className="fas fa-spinner am-spin" />
                                저장 중...
                            </>
                        ) : (
                            <>
                                <i className={`fas ${mode === 'create' ? 'fa-plus' : 'fa-check'}`} />
                                {mode === 'create' ? '등록' : '저장'}
                            </>
                        )}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AuthorityModal;
